import React, { useState } from "react";
import {
    Box,
    Button,
    Paper,
    Stack,
    Typography,
    Alert,
    LinearProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import * as XLSX from "xlsx";
import { getCookie } from "../../../helpers/cookies";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const PAYMENT_LIST_URL = `${API_BASE_URL}/payment/payment-list`;
const BOOKING_LIST_URL = `${API_BASE_URL}/bookings/list`;
const BOOKING_SINGLE_URL = `${API_BASE_URL}/bookings`;
const ACTIVITY_LIST_URL = `${API_BASE_URL}/activity/list`;

const DashboardContainer = () => {
    const [file, setFile] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState({
        type: "",
        message: "",
    });
    const [resultInfo, setResultInfo] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files?.[0];

        setStatus({
            type: "",
            message: "",
        });

        setResultInfo(null);

        if (!selectedFile) {
            setFile(null);
            return;
        }

        const extension = selectedFile.name
            .split(".")
            .pop()
            ?.toLowerCase();

        if (!["xlsx", "xls"].includes(extension)) {
            setFile(null);

            setStatus({
                type: "error",
                message: "Please select an Excel file (.xlsx or .xls).",
            });

            return;
        }

        setFile(selectedFile);
    };

    const handleConvert = async () => {
        if (!file) {
            setStatus({
                type: "error",
                message: "Please select an Excel file first.",
            });

            return;
        }

        setIsConverting(true);
        setProgress(0);

        setStatus({
            type: "info",
            message: "Reading Excel file...",
        });

        setResultInfo(null);

        try {
            const inputRows = await readExcelRows(file);

            const transactionColumn = findTransactionColumn(inputRows);

            if (!transactionColumn) {
                throw new Error(
                    'Could not find the "CCAvenue Ref Number" column in the uploaded Excel.'
                );
            }

            const uploadedColumns = Object.keys(inputRows[0] || {}).filter(
                (column) => column !== transactionColumn
            );

            const transactions = inputRows
                .map((row) =>
                    normalizeTransactionId(row[transactionColumn])
                )
                .filter(Boolean);

            if (!transactions.length) {
                throw new Error(
                    "No CCAvenue Ref Number values were found in the Excel."
                );
            }

            const uniqueTransactions = [...new Set(transactions)];

            setStatus({
                type: "info",
                message: `Found ${uniqueTransactions.length} unique transaction(s). Fetching details...`,
            });

            const reportData = await buildReport(
                uniqueTransactions,
                (completed, total, message) => {
                    setProgress(
                        Math.round((completed / total) * 100)
                    );

                    setStatus({
                        type: "info",
                        message,
                    });
                },
                inputRows,
                transactionColumn,
                uploadedColumns
            );

            const workbook = createWorkbook(
                reportData.rows,
                reportData.activityColumns,
                reportData.eventColumns,
                reportData.uploadedColumns
            );

            const fileName =
                `CCAvenue_Activity_Report_${formatDateForFile(
                    new Date()
                )}.xlsx`;

            XLSX.writeFile(workbook, fileName);

            setProgress(100);

            setResultInfo({
                transactions: uniqueTransactions.length,
                rows: reportData.rows.length,
                activities: reportData.activityColumns.length,
                fileName,
            });

            setStatus({
                type: "success",
                message: `Report generated successfully: ${fileName}`,
            });
        } catch (error) {
            console.error(
                "Excel report generation error:",
                error
            );

            setStatus({
                type: "error",
                message:
                    error?.message ||
                    "Something went wrong while generating the report.",
            });
        } finally {
            setIsConverting(false);
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                p: 3,
                pt: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: "100%",
                    p: 4,
                    borderRadius: 3,
                }}
            >
                <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                >
                    CCAvenue Activity Report
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                >
                    Upload the CCAvenue Excel file and generate
                    the activity payment report.
                </Typography>

                <Stack
                    direction={{
                        xs: "column",
                        md: "row",
                    }}
                    spacing={2}
                    alignItems={{
                        xs: "stretch",
                        md: "center",
                    }}
                >
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        disabled={isConverting}
                        sx={{
                            minWidth: 210,
                        }}
                    >
                        Choose Excel File

                        <input
                            hidden
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                        />
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        onClick={handleConvert}
                        disabled={!file || isConverting}
                        sx={{
                            minWidth: 150,
                        }}
                    >
                        {isConverting
                            ? "Converting..."
                            : "Convert Excel"}
                    </Button>
                </Stack>

                {file && (
                    <Typography sx={{ mt: 2 }}>
                        Selected file:{" "}
                        <b>{file.name}</b>
                    </Typography>
                )}

                {isConverting && (
                    <Box sx={{ mt: 3 }}>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                        />

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                mt: 1,
                                display: "block",
                            }}
                        >
                            {progress}%
                        </Typography>
                    </Box>
                )}

                {status.message && (
                    <Alert
                        severity={
                            status.type || "info"
                        }
                        sx={{ mt: 3 }}
                    >
                        {status.message}
                    </Alert>
                )}

                {resultInfo && (
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: "#f7f9fc",
                        }}
                    >
                        <Typography variant="body2">
                            Transactions:{" "}
                            <b>
                                {resultInfo.transactions}
                            </b>
                        </Typography>

                        <Typography variant="body2">
                            Output rows:{" "}
                            <b>
                                {resultInfo.rows}
                            </b>
                        </Typography>

                        <Typography variant="body2">
                            Activities:{" "}
                            <b>
                                {resultInfo.activities}
                            </b>
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default DashboardContainer;

/* ========================================================= */
/* Excel Reader                                               */
/* ========================================================= */

const readExcelRows = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const buffer = new Uint8Array(
                    event.target.result
                );

                const workbook = XLSX.read(buffer, {
                    type: "array",
                });

                const firstSheetName =
                    workbook.SheetNames[0];

                const worksheet =
                    workbook.Sheets[firstSheetName];

                if (!worksheet) {
                    throw new Error(
                        "The Excel file does not contain a readable sheet."
                    );
                }

                const rows =
                    XLSX.utils.sheet_to_json(
                        worksheet,
                        {
                            defval: "",
                            raw: true,
                        }
                    );

                resolve(rows);
            } catch (error) {
                reject(
                    new Error(
                        `Unable to read the Excel file: ${error.message}`
                    )
                );
            }
        };

        reader.onerror = () =>
            reject(
                new Error(
                    "Unable to read the uploaded Excel file."
                )
            );

        reader.readAsArrayBuffer(file);
    });

/* ========================================================= */
/* Excel Column Helpers                                       */
/* ========================================================= */

const findTransactionColumn = (rows) => {
    if (!rows.length) return null;

    const possibleNames = [
        "CCAvenue Ref Number",
        "CCAvenue Ref No",
        "CCAvenue Reference Number",
        "CCAVENUE REF NUMBER",
        "Transaction Id",
        "Transaction ID",
    ];

    const headers = Object.keys(rows[0]);

    const normalize = (value) =>
        String(value || "")
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");

    const headerMap = new Map(
        headers.map((header) => [
            normalize(header),
            header,
        ])
    );

    for (const name of possibleNames) {
        const found =
            headerMap.get(normalize(name));

        if (found) {
            return found;
        }
    }

    return (
        headers.find((header) => {
            const key = normalize(header);

            return (
                key.includes("ccavenue") &&
                (key.includes("ref") ||
                    key.includes("transaction"))
            );
        }) || null
    );
};

const normalizeTransactionId = (value) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "";
    }

    return String(value).trim();
};

/* ========================================================= */
/* API Helpers                                                */
/* ========================================================= */

const fetchJson = async (url) => {
    // Your ASMC application stores JWT in the "asmc_token" cookie
    const token = getCookie("asmc_token");

    if (!token) {
        throw new Error(
            "Authentication token not found. Please log in again."
        );
    }

    const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    let payload = null;

    try {
        payload = await response.json();
    } catch {
        throw new Error(
            `API returned invalid JSON: ${url}`
        );
    }

    if (!response.ok) {
        throw new Error(
            payload?.message ||
                payload?.error ||
                `API request failed with status ${response.status}`
        );
    }

    if (payload?.success === false) {
        throw new Error(
            payload?.message ||
                "API request failed."
        );
    }

    return payload;
};

const getArrayFromApiResponse = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    const candidates = [
        // Current API response structure
        payload?.result?.result,

        // Other possible response structures
        payload?.result,
        payload?.result?.data,
        payload?.result?.records,
        payload?.result?.docs,
        payload?.result?.list,
        payload?.result?.items,
        payload?.result?.rows,

        payload?.data,
        payload?.data?.records,
        payload?.data?.docs,
        payload?.data?.list,
        payload?.data?.items,
        payload?.data?.rows,
    ];

    for (const item of candidates) {
        if (Array.isArray(item)) {
            return item;
        }
    }

    return [];
};

const getPaymentByTrackingId = async (trackingId) => {
    const params = new URLSearchParams({
        pageNo: "0",
        limit: "10",
        sortBy: "-1",
        sortField: "createdAt",
        keywords: trackingId,
        filter_by: "any_word",
        payment_status: "Success",
    });

    const payload = await fetchJson(
        `${PAYMENT_LIST_URL}?${params.toString()}`
    );

    const records =
        getArrayFromApiResponse(payload);

    for (const payment of records) {
        let paymentResponse = null;

        try {
            paymentResponse =
                payment?.payment_response
                    ? JSON.parse(
                          payment.payment_response
                      )
                    : null;
        } catch {
            paymentResponse = null;
        }

        const ids = [
            payment?.payment_id,
            paymentResponse?.tracking_id,
            paymentResponse?.order_id,
        ]
            .filter(Boolean)
            .map((value) =>
                normalizeTransactionId(value)
            );

        if (ids.includes(trackingId)) {
            return payment;
        }
    }

    return records.length === 1
        ? records[0]
        : null;
};

const getBookingFromList = async (
    bookingId,
    preferredType = "enrollment"
) => {
    const types = [
        ...new Set([
            preferredType,
            "enrollment",
            "booking",
        ]),
    ];

    for (const type of types) {
        const params = new URLSearchParams({
            pageNo: "0",
            limit: "1",
            sortBy: "-1",
            sortField: "createdAt",
            booking_id: String(bookingId),
            type,
        });

        const payload = await fetchJson(
            `${BOOKING_LIST_URL}?${params.toString()}`
        );

        const records =
            getArrayFromApiResponse(payload);

        if (records.length > 0) {
            return records[0];
        }
    }

    return null;
};

const getBookingByMongoId = async (mongoId) => {
    const params = new URLSearchParams({
        _id: String(mongoId),
    });

    const payload = await fetchJson(
        `${BOOKING_SINGLE_URL}?${params.toString()}`
    );

    return (
        getArrayFromApiResponse(payload)[0] ||
        null
    );
};

const getActivityById = async (
    activityId,
    cache
) => {
    if (!activityId) {
        return null;
    }

    const cacheKey = String(activityId);

    if (cache.has(cacheKey)) {
        return cache.get(cacheKey);
    }

    const params = new URLSearchParams({
        activity_id: String(activityId),
    });

    const payload = await fetchJson(
        `${ACTIVITY_LIST_URL}?${params.toString()}`
    );

    const activity =
        getArrayFromApiResponse(payload)[0] ||
        null;

    cache.set(cacheKey, activity);

    return activity;
};

/* ========================================================= */
/* Payment → Booking                                          */
/* ========================================================= */

const resolvePaymentBookings = async (payment) => {
    // The payment API already returns populated bookings_data.
    if (Array.isArray(payment?.bookings_data)) {
        return payment.bookings_data.filter(Boolean);
    }

    // Fallback only when bookings_data is not available.
    const bookingIds = Array.isArray(payment?.booking_id)
        ? payment.booking_id
        : payment?.booking_id
            ? [payment.booking_id]
            : [];

    const bookings = [];

    for (const bookingId of bookingIds) {
        const rawId =
            typeof bookingId === "object" && bookingId !== null
                ? bookingId?._id || bookingId?.booking_id
                : bookingId;

        if (!rawId) continue;

        let booking = null;

        // Mongo ObjectId
        if (
            typeof rawId === "string" &&
            /^[a-fA-F0-9]{24}$/.test(rawId)
        ) {
            booking = await getBookingByMongoId(rawId);
        }

        // Numeric booking ID
        if (!booking && /^\d+$/.test(String(rawId))) {
            booking = await getBookingFromList(
                rawId,
                payment?.booking_type === "booking"
                    ? "booking"
                    : "enrollment"
            );
        }

        if (booking) {
            bookings.push(booking);
        }
    }

    return bookings;
};

/* ========================================================= */
/* Participant Logic                                          */
/* ========================================================= */

const isMongoObjectId = (value) =>
    /^[a-fA-F0-9]{24}$/.test(String(value || "").trim());

const resolveDisplayMemberId = (value) => {
    const candidate = String(value || "").trim();
    return candidate && !isMongoObjectId(candidate) ? candidate : "";
};

const getMemberName = (member) => {
    if (!member) return "";

    return (
        member?.name ||
        member?.full_name ||
        member?.member_name ||
        ""
    );
};

const extractParticipants = (
    booking,
    payment,
    primaryMemberName
) => {
    const participants = [];

    /*
     * 1. Hourly/player based booking
     */
    if (
        Array.isArray(booking?.players) &&
        booking.players.length > 0
    ) {
        booking.players.forEach((player) => {
            const name =
                player?.name ||
                player?.full_name ||
                player?.player_name ||
                "";

            if (!name) return;

            const normalizedName = normalizeName(name);
            const normalizedPrimary = normalizeName(primaryMemberName);

            participants.push({
                name,
                memberId: resolveDisplayMemberId(
                    player?.member_id || player?.memberId
                ),
                secondaryMemberId:
                    player?.secondary_member_id ||
                    player?.secondaryMemberId ||
                    "",
                memberMongoId: isMongoObjectId(player?.member_id)
                    ? player.member_id
                    : "",
                isPrimary:
                    player?.is_primary === true ||
                    player?.primary === true ||
                    normalizedName === normalizedPrimary,
                isMember:
                    player?.is_member === true ||
                    player?.is_member === "Yes",
                raw: player,
            });
        });
    }

    /*
     * 2. Enrollment/family member booking
     *
     * This is the structure present in your API response.
     */
    if (
        participants.length === 0 &&
        Array.isArray(booking?.family_member)
    ) {
        booking.family_member
            .filter(Boolean)
            .forEach((familyMember) => {
                const name =
                    familyMember?.name ||
                    familyMember?.full_name ||
                    familyMember?.member_name ||
                    "";

                if (!name) return;

                participants.push({
                    name,
                    memberId: resolveDisplayMemberId(
                        familyMember?.member_id || familyMember?.memberId
                    ),
                    secondaryMemberId:
                        familyMember?._id ||
                        familyMember?.id ||
                        "",
                    memberMongoId: isMongoObjectId(familyMember?.member_id)
                        ? familyMember.member_id
                        : "",
                    isPrimary: false,
                    isMember: true,
                    raw: familyMember,
                });
            });
    }

    /*
     * 3. Only use primary member when this booking
     *    itself has no participant information.
     *
     *    This prevents SHREENIVAS from incorrectly appearing
     *    for every secondary-member booking.
     */
    if (participants.length === 0) {
        const bookingPrimaryName =
            getMemberName(booking?.member_id) ||
            booking?.member_data?.[0]?.name ||
            booking?.member_data?.name ||
            primaryMemberName ||
            "";

        if (bookingPrimaryName) {
            participants.push({
                name: bookingPrimaryName,
                memberId: resolveDisplayMemberId(
                    booking?.member_id?.member_id ||
                    booking?.memberData?.member_id ||
                    booking?.member_data?.[0]?.member_id
                ),
                secondaryMemberId: "",
                memberMongoId: isMongoObjectId(booking?.member_id?._id)
                    ? booking.member_id._id
                    : "",
                isPrimary: true,
                isMember: true,
                raw: booking,
            });
        }
    }

    return participants.filter(
        (participant) =>
            participant.name &&
            String(participant.name).trim()
    );
};

/* ========================================================= */
/* Amount Logic                                                */
/* ========================================================= */

const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : 0;
};

const resolveParticipantAmount = (
    participant,
    booking,
    participants,
    payment
) => {
    const raw = participant?.raw || {};

    /*
     * 1. Player-specific amount
     */
    const playerAmount =
        raw?.amount ??
        raw?.price ??
        raw?.fee ??
        raw?.fees ??
        raw?.paid_amount ??
        raw?.amount_paid ??
        raw?.final_amount;

    if (
        playerAmount !== undefined &&
        playerAmount !== null &&
        playerAmount !== ""
    ) {
        return toNumber(playerAmount);
    }

    /*
     * 2. Hourly booking
     *
     * If the booking has multiple players and there is no
     * player-specific amount, the booking total belongs to
     * the booking as a whole, so add it only once.
     */
    const isHourlyBooking =
        payment?.booking_type === "booking" ||
        booking?.type === "booking" ||
        String(
            booking?.fees_breakup?.plan_type || ""
        ).toLowerCase() === "booking";

    if (isHourlyBooking) {
        return participants[0] === participant
            ? toNumber(booking?.total_amount)
            : 0;
    }

    /*
     * 3. Family/enrollment booking
     */
    if (
        Array.isArray(booking?.family_member) &&
        booking.family_member.length > 0
    ) {
        if (participants.length === 1) {
            return toNumber(booking?.total_amount);
        }

        return participants[0] === participant
            ? toNumber(booking?.total_amount)
            : 0;
    }

    /*
     * 4. Normal single-participant booking
     */
    if (participants.length === 1) {
        return toNumber(booking?.total_amount);
    }

    return 0;
};

const resolveMembershipFee = (
    payment,
    participant,
    isTransactionPrimaryRow = false,
    fallbackMembershipAmount = 0
) => {
    const membershipAmount = Number(
        payment?.membership_amount ||
        payment?.membershipAmount ||
        fallbackMembershipAmount ||
        0
    );

    if (!Number.isFinite(membershipAmount) || membershipAmount <= 0) {
        return 0;
    }

    const secondaryMemberId = payment?.secondary_member_id;

    /*
     * A payment with secondary_member_id belongs only to that
     * secondary member. Match by the internal secondary-member id
     * (never displayed in the Excel) and fall back to the family name
     * when necessary.
     */
    if (secondaryMemberId) {
        if (
            participant?.secondaryMemberId &&
            String(participant.secondaryMemberId) === String(secondaryMemberId)
        ) {
            return membershipAmount;
        }

        const familyMembers =
            payment?.member_data?.[0]?.family_details || [];

        const secondaryMember = familyMembers.find(
            (family) =>
                String(family?._id) === String(secondaryMemberId) ||
                String(family?.id) === String(secondaryMemberId)
        );

        if (
            secondaryMember &&
            normalizeName(secondaryMember?.name) ===
                normalizeName(participant?.name)
        ) {
            return membershipAmount;
        }

        return 0;
    }

    /*
     * Without secondary_member_id, this is a primary-member
     * membership payment. Put it only on the first transaction row.
     * This avoids giving the same fee to a duplicate participant row
     * when the primary member is also listed as a player.
     */
    return isTransactionPrimaryRow ? membershipAmount : 0;
};

/* ========================================================= */
/* Report Builder                                             */
/* ========================================================= */

const buildReport = async (
    transactionIds,
    onProgress,
    inputRows = [],
    transactionColumn = null,
    uploadedColumns = []
) => {
    const activityCache = new Map();

    const rows = [];
    const activityColumns = [];
    const activitySet = new Set();
    const eventColumns = [];
    const eventSet = new Set();

    const uploadedRowsByTransaction = new Map();
    inputRows.forEach((sourceRow) => {
        const sourceTransactionId = normalizeTransactionId(
            sourceRow?.[transactionColumn]
        );

        if (sourceTransactionId && !uploadedRowsByTransaction.has(sourceTransactionId)) {
            uploadedRowsByTransaction.set(sourceTransactionId, sourceRow);
        }
    });

    for (
        let index = 0;
        index < transactionIds.length;
        index++
    ) {
        const transactionId =
            transactionIds[index];

        onProgress(
            index,
            transactionIds.length,
            `Processing transaction ${
                index + 1
            } of ${
                transactionIds.length
            }: ${transactionId}`
        );

        const payment =
            await getPaymentByTrackingId(
                transactionId
            );

        if (!payment) {
            rows.push({
                "Transaction Id":
                    transactionId,

                "Primary member Name": "",

                "Secondary Members":
                    "Payment not found",

                "Total amount Paid": "",
            });

            continue;
        }

        const primaryMemberName =
            payment?.member_data?.[0]?.name ||
            payment?.memberData?.name ||
            payment?.name ||
            "";

        const primaryMemberId = resolveDisplayMemberId(
            payment?.member_data?.[0]?.member_id ||
            payment?.memberData?.member_id
        );

        const bookings =
            await resolvePaymentBookings(
                payment
            );

        const participantRows = [];

        for (const booking of bookings) {
            const bookingActivityId =
                booking?.activity_id?._id ||
                booking?.activity_id;

            let activity = null;

            if (Array.isArray(payment?.activity_data)) {
                activity =
                    payment.activity_data.find(
                        (item) =>
                            String(item?._id) ===
                                String(bookingActivityId) ||
                            String(item?.activity_id) ===
                                String(bookingActivityId)
                    ) || null;
            }

            if (!activity?.name && booking?.activity_id?.name) {
                activity = booking.activity_id;
            }

            if (!activity?.name && bookingActivityId) {
                activity = await getActivityById(
                    bookingActivityId,
                    activityCache
                );
            }

            const baseActivityName =
                activity?.name ||
                booking?.activity_name ||
                "Unknown Activity";

            /*
             * Direct/hourly bookings are stored as booking type
             * "booking". Display them as a separate dynamic column
             * so normal activity enrollment and hourly bookings do
             * not get mixed together.
             *
             * Example: HOURLY(Badminton)
             */
            const isHourlyBooking =
                payment?.booking_type === "booking" ||
                booking?.type === "booking" ||
                String(booking?.fees_breakup?.plan_type || "").toLowerCase() ===
                    "booking";

            const activityName = isHourlyBooking
                ? `HOURLY(${baseActivityName})`
                : baseActivityName;

            if (!activitySet.has(activityName)) {
                activitySet.add(activityName);
                activityColumns.push(activityName);
            }

            const participants =
                extractParticipants(
                    booking,
                    payment,
                    primaryMemberName
                );

            participants.forEach(
                (participant) => {
                    const amount =
                        resolveParticipantAmount(
                            participant,
                            booking,
                            participants,
                            payment
                        );

                    participantRows.push({
                        name: participant.name,
                        memberId: participant.memberId || "",
                        secondaryMemberId: participant.secondaryMemberId || "",
                        memberMongoId: participant.memberMongoId || "",
                        isPrimaryParticipant:
                            participant.isPrimary === true,
                        activityName,
                        amount,
                    });
                }
            );
        }

        /*
         * Event payments are already populated by the payment-list
         * aggregation as event_bookings_data + event_data.
         * Treat every real event like a dynamic activity column:
         * Event(Event Name)
         *
         * Event payments are transaction-level, so the amount is
         * attached to the first/primary report row below.
         */
        const eventAmounts = {};
        const eventBookings = Array.isArray(payment?.event_bookings_data)
            ? payment.event_bookings_data.filter(Boolean)
            : [];
        const eventRecords = Array.isArray(payment?.event_data)
            ? payment.event_data.filter(Boolean)
            : [];

        eventBookings.forEach((eventBooking) => {
            const eventRecord = eventRecords.find(
                (event) =>
                    String(event?._id) === String(eventBooking?.event_id) ||
                    String(event?.event_id) === String(eventBooking?.event_id)
            );

            const eventName =
                eventRecord?.event_name ||
                eventBooking?.event_name ||
                eventBooking?.events_data?.event_name ||
                "";

            if (!eventName) return;

            const eventColumn = `Event(${eventName})`;

            if (!eventSet.has(eventColumn)) {
                eventSet.add(eventColumn);
                eventColumns.push(eventColumn);
            }

            const paymentEventAmount =
                eventBooking?.amount_paid !== undefined &&
                eventBooking?.amount_paid !== null &&
                eventBooking?.amount_paid !== ""
                    ? toNumber(eventBooking.amount_paid)
                    : eventBookings.length === 1
                      ? toNumber(payment?.amount_paid)
                      : 0;

            eventAmounts[eventColumn] =
                Number(eventAmounts[eventColumn] || 0) +
                paymentEventAmount;
        });

        /*
         * If there are no bookings, still create
         * a transaction row.
         */
        if (!participantRows.length) {
            participantRows.push({
                name: primaryMemberName,
                memberId: primaryMemberId,
                secondaryMemberId: "",
                memberMongoId: "",
                isPrimaryParticipant: true,
                activityName: "",
                amount: 0,
            });
        }

        const totalAmountPaid =
            toNumber(payment?.amount_paid);

        const hasActivityAmount = participantRows.some(
            (participant) => Number(participant.amount || 0) > 0
        );

        const hasEventAmount = Object.values(eventAmounts).some(
            (amount) => Number(amount || 0) > 0
        );

        /*
        * If this transaction has no activity/event payment,
        * the complete amount is a membership fee.
        */
        const activityTotal = participantRows.reduce(
            (sum, participant) =>
                sum + Number(participant.amount || 0),
            0
        );

        const eventTotal = Object.values(eventAmounts).reduce(
            (sum, amount) => sum + Number(amount || 0),
            0
        );

        /*
         * Prefer the explicit membership field when the API provides it.
         * Otherwise calculate the membership portion as the remaining
         * amount after activity + event charges.
         */
        const explicitMembershipFee = Number(
            payment?.membership_amount ||
            payment?.membershipAmount ||
            0
        );

        const calculatedMembershipFee = Math.max(
            0,
            totalAmountPaid - activityTotal - eventTotal
        );

        const transactionMembershipFee =
            explicitMembershipFee > 0
                ? explicitMembershipFee
                : calculatedMembershipFee;

        /*
        * Group all activities belonging to the same person
        * into one Excel row.
        */
        const groupedParticipants = new Map();

        participantRows.forEach((participant, participantIndex) => {
            const nameKey = normalizeName(participant.name);

            if (!nameKey) return;

            /*
             * Group the same participant across activities, but keep a
             * separately listed primary participant with the same name
             * as its own row when it has a different internal identity.
             */
            const identityKey =
                participant.secondaryMemberId
                    ? `secondary:${String(participant.secondaryMemberId)}`
                    : participant.memberMongoId
                      ? `mongo:${String(participant.memberMongoId)}`
                      : participant.memberId
                        ? `member:${String(participant.memberId)}`
                        : `${participant.isPrimaryParticipant ? "primary" : "participant"}:${nameKey}`;

            if (!groupedParticipants.has(identityKey)) {
                groupedParticipants.set(identityKey, {
                    name: participant.name,
                    memberId: participant.memberId || "",
                    secondaryMemberId:
                        participant.secondaryMemberId || "",
                    memberMongoId:
                        participant.memberMongoId || "",
                    isPrimaryParticipant:
                        participant.isPrimaryParticipant === true,
                    activities: {},
                    events: {},
                    membershipFee: 0,
                    firstParticipantIndex: participantIndex,
                });
            }

            const grouped = groupedParticipants.get(identityKey);

            if (participant.activityName) {
                grouped.activities[participant.activityName] =
                    Number(
                        grouped.activities[participant.activityName] || 0
                    ) + Number(participant.amount || 0);
            }
        });

        /*
         * Allocate membership fees to the correct row:
         * - secondary_member_id -> matching secondary participant only
         * - no secondary_member_id -> transaction's primary row only
         */
        const groupedParticipantListForFees = Array.from(
            groupedParticipants.values()
        );

        const primaryParticipantIndex =
            groupedParticipantListForFees.findIndex(
                (participant) =>
                    participant.isPrimaryParticipant === true
            );

        const fallbackPrimaryIndex =
            primaryParticipantIndex >= 0
                ? primaryParticipantIndex
                : 0;

        groupedParticipants.forEach((participant) => {
            participant.membershipFee =
                resolveMembershipFee(
                    payment,
                    participant,
                    false,
                    transactionMembershipFee
                );
        });

        /*
        * Membership-only transaction:
        * assign amount_paid to the primary transaction row.
        */
        if (transactionMembershipFee > 0 && !payment?.secondary_member_id) {
            const primaryParticipant =
                groupedParticipantListForFees.find(
                    (participant) =>
                        participant.isPrimaryParticipant === true
                ) || groupedParticipantListForFees[0];

            if (primaryParticipant) {
                primaryParticipant.membershipFee =
                    transactionMembershipFee;
            }
        }

        /*
         * Event payments are transaction-level. Put each event amount
         * on the primary participant row so the same transaction is
         * not duplicated across multiple participant rows.
         */
        const groupedParticipantList =
            groupedParticipantListForFees;

        if (groupedParticipantList.length > 0) {
            const primaryParticipant =
                groupedParticipantList.find(
                    (participant) =>
                        normalizeName(participant.name) ===
                        normalizeName(primaryMemberName)
                ) || groupedParticipantList[0];

            primaryParticipant.events = eventAmounts;
        }

        /*
         * Create one row per player,
         * but one transaction group.
         */
        const finalParticipants = groupedParticipantList;

        finalParticipants.forEach(
            (participant, rowIndex) => {
                const row = {
                    "Transaction Id":
                        rowIndex === 0
                            ? transactionId
                            : "",

                    "Member ID":
                        rowIndex === 0
                            ? primaryMemberId || "-"
                            : "-",

                    "Primary member Name":
                        rowIndex === 0
                            ? primaryMemberName
                            : "",

                    "Secondary Members":
                        participant.name,

                    "Membership Fees":
                        participant.membershipFee > 0
                            ? participant.membershipFee
                            : "-",
                };

                activityColumns.forEach(
                    (activity) => {
                        const amount =
                            Number(
                                participant.activities?.[activity] || 0
                            );

                        row[activity] =
                            amount > 0
                                ? amount
                                : "-";
                    }
                );

                eventColumns.forEach((eventColumn) => {
                    const amount = Number(
                        participant.events?.[eventColumn] || 0
                    );

                    row[eventColumn] =
                        amount > 0
                            ? amount
                            : "-";
                });

                row["Total amount Paid"] =
                    rowIndex === 0
                        ? totalAmountPaid
                        : "";

                // Preserve every additional column from the uploaded Excel
                // exactly as provided, placing them after Total amount Paid.
                if (rowIndex === 0) {
                    const sourceRow = uploadedRowsByTransaction.get(transactionId) || {};
                    uploadedColumns.forEach((column) => {
                        row[column] = sourceRow[column] ?? "";
                    });
                } else {
                    uploadedColumns.forEach((column) => {
                        row[column] = "";
                    });
                }

                rows.push(row);
            }
        );
    }

    onProgress(
        transactionIds.length,
        transactionIds.length,
        "Preparing Excel file..."
    );

    return {
        rows,
        activityColumns,
        eventColumns,
        uploadedColumns,
    };
};

/* ========================================================= */
/* Create Workbook                                             */
/* ========================================================= */

const createWorkbook = (
    rows,
    activityColumns,
    eventColumns,
    uploadedColumns = []
) => {
    const headers = [
        "Transaction Id",
        "Member ID",
        "Primary member Name",
        "Secondary Members",
        "Membership Fees",
        ...activityColumns,
        ...eventColumns,
        "Total amount Paid",
        ...uploadedColumns,
    ];

    const worksheetData = [
        headers,
        ...rows.map((row) =>
            headers.map(
                (header) =>
                    row[header] ?? ""
            )
        ),
    ];

    const worksheet =
        XLSX.utils.aoa_to_sheet(
            worksheetData
        );

    worksheet["!cols"] = [
        { wch: 20 }, // Transaction Id
        { wch: 20 }, // Member ID
        { wch: 28 }, // Primary member Name
        { wch: 30 }, // Secondary Members
        { wch: 18 }, // Membership Fees
        ...activityColumns.map(() => ({
            wch: 18,
        })),
        ...eventColumns.map(() => ({
            wch: 30,
        })),
        { wch: 18 }, // Total amount Paid
        ...uploadedColumns.map((column) => ({
            wch: Math.min(40, Math.max(14, String(column || "").length + 2)),
        })),
    ];

    const workbook =
        XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Activity Report"
    );

    return workbook;
};

/* ========================================================= */
/* Date Helper                                                 */
/* ========================================================= */

const normalizeName = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

const formatDateForFile = (date) => {
    const year =
        date.getFullYear();

    const month = String(
        date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
        date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
};